import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { supabase, isConfigured } from '../lib/supabase';
import { flat as staticFlat, canView, groupsFrom, sectionsFrom, audienceOf, audiencesFromScope, PUBLIC_AUDIENCES } from '../nav';
import { loadStaticDoc } from '../lib/content';
import { feedEnabled, fetchList, fetchDoc, fetchBundle, clearFeedCache } from '../lib/docsFeed';

// 문서 원장·본문의 단일 진입점. 화면은 nav.js 나 content.js 를 직접 부르지 않고 여기서 받는다.
//
//   피드 모드 (REACT_APP_DOCS_FEED_URL 있음)
//     원장·본문·열람 범위 전부 docs-feed 함수(= admin 「레이블리 가이드」 표의 게시본)가 준다.
//     역할 판정도 서버가 한다 — 클라이언트는 토큰만 보낸다. 판정 실패는 public 이다(fail-closed).
//   정적 모드 (환경 변수 없음 · 프리뷰 빌드)
//     nav.js 목록 + public/content/*.md (프리뷰는 window.__DOCS__). 열람 범위는 AuthContext 가 준다.
//
// 전환 순서와 이유: docs/ops/guide-editing-handover.md §3.

const DocsContext = createContext(null);

const VIEW_AUDIENCE_KEY = 'docs-view-audience';

function readSavedAudience() {
  try {
    const v = window.sessionStorage.getItem(VIEW_AUDIENCE_KEY);
    return v === 'creator' || v === 'rights' ? v : null;
  } catch (e) {
    return null;
  }
}

// 두 대상 문서를 함께 받는 사람인가 — 피드 모드는 서버 scope, 정적 모드는 audiences 로 본다.
function scopeAll(feedScope, audiences) {
  if (feedScope) return feedScope === 'all' || feedScope === 'internal'; // internal = 내부 직원(2026-10-15) — 겸직(all)처럼 두 대상을 함께 받는다
  return Boolean(audiences && audiences.has('creator') && audiences.has('rights'));
}

async function getAccessToken() {
  if (!isConfigured || !supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch (e) {
    return null;
  }
}

export function DocsProvider({ children }) {
  const auth = useAuth();
  const mode = feedEnabled ? 'feed' : 'static';

  // ── 피드 모드 상태 ──
  const [feed, setFeed] = useState({ status: 'loading', scope: 'public', pages: [], error: null, key: null });
  const authKey = auth.authKey;

  const loadList = useCallback(async () => {
    if (!feedEnabled) return;
    setFeed((f) => (f.key === authKey && f.status === 'ready' ? f : { ...f, status: 'loading', error: null }));
    try {
      const token = auth.signedIn ? await getAccessToken() : null;
      const data = await fetchList(token, authKey);
      const pages = (data.pages || []).map((p) => ({ ...p, visible: Boolean(p.visible) }));
      setFeed({ status: 'ready', scope: data.scope || 'public', pages, error: null, key: authKey });
    } catch (error) {
      setFeed({ status: 'error', scope: 'public', pages: [], error, key: authKey });
    }
  }, [authKey, auth.signedIn]);

  useEffect(() => {
    if (!feedEnabled) return undefined;
    if (auth.status === 'loading') return undefined;
    clearFeedCache(); // 로그인·로그아웃으로 사용자가 바뀌면 이전 범위의 응답을 버린다
    loadList();
    return undefined;
  }, [auth.status, loadList]);

  // ── 두 모드를 같은 모양으로 ──
  const audiences = useMemo(() => {
    if (mode === 'feed') return feed.status === 'ready' ? audiencesFromScope(feed.scope) : PUBLIC_AUDIENCES;
    return auth.audiences || PUBLIC_AUDIENCES;
  }, [mode, feed.status, feed.scope, auth.audiences]);

  const pages = useMemo(() => {
    if (mode === 'feed') return feed.pages;
    return staticFlat.map((p) => ({ ...p, visible: canView(p, audiences) }));
  }, [mode, feed.pages, audiences]);

  // ── 대상 보기 (2026-09-07) ──
  // 관리자·겸직(scope 'all')만 두 대상 문서를 함께 받는다. 그때 한 화면에 섞여 나오지 않도록
  // ① 「크리에이터 / 영상권리사」로 좁혀 보는 스위치와 ② 좁히지 않았을 때의 대상별 구획을 준다.
  // 자기 대상만 보는 사람에게는 스위치가 뜨지 않고 화면도 종전과 같다.
  const canSwitchAudience = scopeAll(mode === 'feed' ? feed.scope : null, auth.audiences);
  const [viewAudience, setViewAudienceState] = useState(() => readSavedAudience());
  const setViewAudience = useCallback((next) => {
    setViewAudienceState(next);
    try {
      if (next) window.sessionStorage.setItem(VIEW_AUDIENCE_KEY, next);
      else window.sessionStorage.removeItem(VIEW_AUDIENCE_KEY);
    } catch (e) { /* 저장소 막힘 — 이번 방문에만 적용된다 */ }
  }, []);

  const allVisible = useMemo(() => pages.filter((p) => p.visible), [pages]);
  const visiblePages = useMemo(() => {
    if (!canSwitchAudience || !viewAudience) return allVisible;
    // 공통 문서(약관·공개)는 어느 쪽을 보든 남긴다 — 좁혀 본다고 약관이 사라지면 안 된다.
    return allVisible.filter((p) => {
      const a = audienceOf(p);
      return a === viewAudience || a === 'both' || a === 'public';
    });
  }, [allVisible, canSwitchAudience, viewAudience]);
  const groups = useMemo(() => groupsFrom(visiblePages), [visiblePages]);
  // 전체를 볼 때만 대상별로 나눠 준다. 그 외에는 구획 하나(종전 화면).
  const sections = useMemo(
    () => (canSwitchAudience && !viewAudience
      ? sectionsFrom(visiblePages, groupsFrom)
      : [{ audience: null, label: null, groups }]),
    [canSwitchAudience, viewAudience, visiblePages, groups]
  );
  const bySlug = useMemo(() => Object.fromEntries(pages.map((p) => [p.slug, p])), [pages]);

  const status = mode === 'feed' ? feed.status : (auth.status === 'loading' ? 'loading' : 'ready');
  const scope = mode === 'feed'
    ? feed.scope
    : (audiences.has('internal') ? 'internal' : audiences.has('creator') && audiences.has('rights') ? 'all' : audiences.has('rights') ? 'rights' : audiences.has('creator') ? 'creator' : 'public');

  const loadDoc = useCallback(async (slug) => {
    if (mode !== 'feed') return loadStaticDoc(slug);
    const token = auth.signedIn ? await getAccessToken() : null;
    const data = await fetchDoc(slug, token, authKey);
    return String(data.body || '');
  }, [mode, auth.signedIn, authKey]);

  // 검색 팔레트용 — 볼 수 있는 문서 전부를 [{ page, text }] 로.
  const loadBundle = useCallback(async () => {
    if (mode !== 'feed') {
      return Promise.all(visiblePages.map((page) => loadStaticDoc(page.slug).then((text) => ({ page, text })).catch(() => ({ page, text: '' }))));
    }
    const token = auth.signedIn ? await getAccessToken() : null;
    const data = await fetchBundle(token, authKey);
    return (data.pages || []).map((p) => ({ page: { ...p, body: undefined, visible: true }, text: String(p.body || '') }));
  }, [mode, auth.signedIn, authKey, visiblePages]);

  const value = useMemo(
    () => ({
      mode, status, error: mode === 'feed' ? feed.error : null, scope, audiences, pages,
      visiblePages, groups, sections, bySlug, loadDoc, loadBundle, reload: loadList,
      canSwitchAudience, viewAudience, setViewAudience,
    }),
    [mode, status, feed.error, scope, audiences, pages, visiblePages, groups, sections, bySlug,
      loadDoc, loadBundle, loadList, canSwitchAudience, viewAudience, setViewAudience]
  );
  return <DocsContext.Provider value={value}>{children}</DocsContext.Provider>;
}

export function useDocs() {
  return useContext(DocsContext);
}
